import { BookOpen, Calendar, FileText, HelpCircle, Home, NotebookTabs } from 'lucide-react'
import { Link, useLocation } from 'react-router'
import { cn } from '#app/utils/misc.tsx'
import { type TabKey } from '#app/utils/tab-visibility.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { AdminTabBar } from './admin/admin-tab-bar.tsx'

const AUTH_ROUTES = new Set([
	'/login',
	'/signup',
	'/forgot-password',
	'/reset-password',
	'/verify',
	'/onboarding',
])

/**
 * The engagements fiche carries its own top bar (back / category / position /
 * share) in place of the app chrome, per the « Engagements Options » design.
 * Matches `/engagements/<slug>` only — not the index, not `<slug>/history`.
 */
export function isEngagementFiche(pathname: string): boolean {
	return /^\/engagements\/[^/]+$/.test(pathname)
}

export function isTabActive(pathname: string, tabPath: string): boolean {
	if (tabPath === '/') return pathname === '/'
	return pathname === tabPath || pathname.startsWith(tabPath + '/')
}

const tabs = [
	{ path: '/', label: 'Accueil', icon: Home },
	{ path: '/lignes', label: 'Lignes', icon: FileText, tabKey: 'lignes' },
	{ path: '/cartes', label: 'Cartes', icon: NotebookTabs, tabKey: 'cartes' },
	{ path: '/engagements', label: 'Engagements', icon: BookOpen, tabKey: 'plateforme' },
	{ path: '/evenements', label: 'Événements', icon: Calendar, tabKey: 'evenements' },
	{ path: '/support', label: 'Soutien', icon: HelpCircle },
] as const satisfies ReadonlyArray<{
	path: string
	label: string
	icon: typeof Home
	tabKey?: TabKey
}>

export function MobileTabBar({ visibleTabs }: { visibleTabs: TabKey[] }) {
	const user = useOptionalUser()
	const { pathname } = useLocation()

	if (!user) return null
	if (pathname.startsWith('/admin')) return <AdminTabBar />
	if (AUTH_ROUTES.has(pathname)) return null
	if (pathname.startsWith('/cartes/terrain')) return null

	// The fiche is immersive on phones only, so the bar is hidden by breakpoint
	// rather than unmounted — from `md` up the fiche is a normal page under the
	// site header, and that's exactly the width range where the bar is still the
	// only navigation (it only gives way to the desktop nav at `xl`).
	const hiddenOnPhone = isEngagementFiche(pathname)

	const visibleSet = new Set(visibleTabs)
	const filteredTabs = tabs.filter(
		(t) => !('tabKey' in t) || visibleSet.has(t.tabKey),
	)

	return (
		<nav
			aria-label="Navigation principale"
			className={cn(
				'border-border/60 bg-background/75 fixed inset-x-0 bottom-0 z-40 flex border-t pb-[env(safe-area-inset-bottom)] backdrop-blur-xl backdrop-saturate-150 xl:hidden',
				hiddenOnPhone && 'max-md:hidden',
			)}
		>
			{filteredTabs.map(({ path, label, icon: Icon }) => {
				const active = isTabActive(pathname, path)
				return (
					<Link
						key={path}
						to={path}
						aria-current={active ? 'page' : undefined}
						className={`flex h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-medium leading-tight transition-all active:scale-[0.92] ${active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
							}`}
					>
						<Icon
							className={`h-5 w-5 shrink-0 transition-transform ${active ? 'scale-110' : ''}`}
							aria-hidden="true"
						/>
						<span className="text-center">{label}</span>
					</Link>
				)
			})}
		</nav>
	)
}
